import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { useArticlesNeedingAttention } from "../../hooks/useArticles";
import { useHelpdeskNavigation } from "../../hooks/useHelpdeskNavigation";
import { useDateFormat } from "../../hooks/useDateFormat";
import { ArticleAttentionReason, type ArticleNeedsAttentionItem } from "../../types/articles";

interface NeedsAttentionCardProps {
  organizationIds?: string[];
  // Shown next to each article when several organizations are listed
  getOrganizationName?: (organizationId: string) => string;
}

const COLLAPSED_COUNT = 5;

function ReasonBadges({ item }: { item: ArticleNeedsAttentionItem }) {
  const { formatDateOnly } = useDateFormat();
  const votes = item.helpfulVotes + item.notHelpfulVotes;
  return (
    <div className="flex flex-wrap gap-1">
      {item.reasons.includes(ArticleAttentionReason.NegativeFeedback) && (
        <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 text-[11px]">
          Unhelpful: {item.helpfulVotes} of {votes} found it helpful
        </Badge>
      )}
      {item.reasons.includes(ArticleAttentionReason.DueForReview) && (
        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 text-[11px]">
          Review due{item.reviewDueAt ? ` since ${formatDateOnly(item.reviewDueAt)}` : ""}
        </Badge>
      )}
      {item.reasons.includes(ArticleAttentionReason.NotRead) && (
        <Badge variant="outline" className="text-[11px]">
          Not read in 90 days
        </Badge>
      )}
    </div>
  );
}

/**
 * Published articles someone should look at: readers aren't finding them helpful, they're due for review (the
 * organization's review interval), or nobody has read them lately. Reviewing an article (or publishing changes to it)
 * takes it off for the first two.
 */
export function NeedsAttentionCard({ organizationIds, getOrganizationName }: NeedsAttentionCardProps) {
  const { getPath } = useHelpdeskNavigation();
  const { data, isLoading } = useArticlesNeedingAttention(organizationIds);
  const [expanded, setExpanded] = useState(false);

  const items = data?.items ?? [];
  const shown = expanded ? items : items.slice(0, COLLAPSED_COUNT);
  const showOrganization = !!getOrganizationName && new Set(items.map((i) => i.article.organizationId)).size > 1;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          Needs Attention
          {data && data.totalCount > 0 && <Badge variant="secondary">{data.totalCount}</Badge>}
        </CardTitle>
        <CardDescription>
          Published articles readers aren't finding helpful, that are due for review, or that nobody has read lately.
          Mark an article as reviewed once it's checked.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-10 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden="true" />
            Nothing needs attention right now.
          </p>
        ) : (
          <>
            <ul className={`divide-y divide-border ${expanded ? "max-h-96 overflow-y-auto" : ""}`}>
              {shown.map((item) => (
                <li key={item.article.id} className="py-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={getPath.article(item.article.id)}
                      className="text-sm font-medium text-foreground hover:underline truncate block"
                      title={item.article.title}
                    >
                      {item.article.title}
                    </Link>
                    <p className="text-xs text-muted-foreground truncate">
                      {item.article.collectionPath || item.article.collectionName || "No collection"}
                      {showOrganization ? ` · ${getOrganizationName!(item.article.organizationId)}` : ""}
                    </p>
                  </div>
                  <ReasonBadges item={item} />
                </li>
              ))}
            </ul>
            {items.length > COLLAPSED_COUNT && (
              <Button variant="ghost" size="sm" className="mt-2" onClick={() => setExpanded((e) => !e)}>
                {expanded
                  ? "Show fewer"
                  : `Show all ${items.length}${data && data.totalCount > items.length ? ` of ${data.totalCount}` : ""}`}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
